import React, { useState } from 'react';
import API_BASE_URL from './config';

const OnboardingStep2 = ({ formData, updateFormData, onNext, onBack }) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [dragActive, setDragActive] = useState(false);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileUpload(e.dataTransfer.files[0]);
        }
    };

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFileUpload(e.target.files[0]);
        }
    };

    const handleFileUpload = async (file) => {
        // Validate file type
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            setError('Only PNG, JPEG, and SVG images are allowed');
            return;
        }

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('File size must be less than 5MB');
            return;
        }

        setUploading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_BASE_URL}/api/onboarding/upload-logo`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            if (!response.ok) {
                throw new Error('Failed to upload logo');
            }

            const data = await response.json();
            updateFormData({ logo_url: data.logo_url });
        } catch (err) {
            setError(err.message || 'Failed to upload logo');
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = () => {
        updateFormData({ logo_url: null });
    };

    return (
        <div className="wizard-step">
            <div className="step-header">
                <h2>Company Branding</h2>
                <p>Upload your company logo <span className="optional-label">(optional)</span></p>
            </div>

            <div className="form-group">
                {!formData.logo_url ? (
                    <div
                        className={`drop-zone ${dragActive ? 'drag-active' : ''}`}
                        style={{
                            border: '2px dashed #ccc',
                            borderRadius: '8px',
                            padding: '40px',
                            textAlign: 'center',
                            background: dragActive ? '#f0f8ff' : '#f9f9f9',
                            cursor: 'pointer'
                        }}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                    >
                        {uploading ? (
                            <div>
                                <div className="loading-spinner"></div>
                                <p>Uploading...</p>
                            </div>
                        ) : (
                            <>
                                <div style={{ fontSize: '48px', marginBottom: '20px' }}>📁</div>
                                <p>Drag and drop your logo here</p>
                                <p style={{ color: '#999', margin: '10px 0' }}>or</p>
                                <label htmlFor="logo-upload" style={{ cursor: 'pointer' }}>
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            padding: '10px 20px',
                                            background: '#007bff',
                                            color: 'white',
                                            borderRadius: '4px'
                                        }}
                                    >
                                        Choose File
                                    </span>
                                    <input
                                        id="logo-upload"
                                        type="file"
                                        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                                        onChange={handleFileSelect}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                                <p style={{ color: '#999', marginTop: '10px', fontSize: '12px' }}>
                                    PNG, JPEG, or SVG, max 5MB
                                </p>
                            </>
                        )}
                    </div>
                ) : (
                    <div
                        style={{
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            padding: '20px',
                            textAlign: 'center',
                            background: 'var(--bg-secondary)'
                        }}
                    >
                        <div style={{ marginBottom: '10px', color: 'var(--text-secondary)', fontSize: '14px' }}>
                            Company Logo Preview
                        </div>
                        <img
                            src={`${API_BASE_URL}${formData.logo_url}`}
                            alt="Company logo"
                            style={{
                                maxWidth: '300px',
                                maxHeight: '200px',
                                marginBottom: '20px',
                                objectFit: 'contain',
                                display: 'block',
                                margin: '0 auto 20px'
                            }}
                            onLoad={() => {
                                console.log('[Onboarding] Logo loaded successfully:', formData.logo_url);
                            }}
                            onError={(e) => {
                                console.error('[Onboarding] Logo failed to load:', formData.logo_url);
                                console.error('[Onboarding] Full URL:', `${API_BASE_URL}${formData.logo_url}`);
                                // Show placeholder instead of hiding
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"%3E%3Crect width="300" height="200" fill="%23f0f0f0"/%3E%3Ctext x="150" y="100" text-anchor="middle" dy=".3em" font-family="Arial" font-size="14" fill="%23999"%3ELogo Preview%3C/text%3E%3C/svg%3E';
                            }}
                        />
                        <div>
                            <button
                                onClick={handleRemove}
                                style={{
                                    padding: '8px 16px',
                                    background: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Remove Logo
                            </button>
                        </div>
                    </div>
                )}

                {error && <div className="error-message">{error}</div>}
            </div>

            <div className="step-footer">
                <button onClick={onBack} className="btn-back">
                    ← Back
                </button>
                <button onClick={onNext} className="btn-next">
                    Next →
                </button>
            </div>
        </div>
    );
};

export default OnboardingStep2;

